import React, { useState, useEffect } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Button,
  Modal,
  Box,
  TextField,
  List,
  ListItem,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";  
import CloseIcon from "@mui/icons-material/Close";
import CountPopup from "./CountPopUp";
import api from "../../services/api";

function FilterPopover({
  open,
  onClose,
  column,
  dbName,
  baseTable,
  joins,
  filterConditions,
  onFilterChange,
}) {
  const [search, setSearch] = useState("");
  const [uniqueValues, setUniqueValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);

  // Fetch column data when modal opens
  useEffect(() => {
    if (open) {
      fetchColumnData();
    } else {
      // Reset when closed
      setSearch("");
      setError("");
    }
  }, [open]);

  const fetchColumnData = async () => {
    setLoading(true);
    setError("");

    try {
      // Build filter conditions excluding current column
      const otherFilters = {};
      Object.keys(filterConditions).forEach((col) => {
        if (
          col !== column &&
          filterConditions[col] &&
          filterConditions[col].length > 0
        ) {
          otherFilters[col] = filterConditions[col];
        }
      });

      // console.log("Fetching column data with filters:", otherFilters);

      const response = await api.post("/schedule_email/get_column_data", {
        db_name: dbName,
        base_table: baseTable,
        joins: joins,
        column_name: column,
        filter_conditions: otherFilters,
      });

      // console.log("Column data response:", response.data);

      if (response.data.success) {
        if (!response.data.is_filterable) {
          setError(response.data.message);
          setUniqueValues([]);
          setSelectedValues([]);
        } else {
          const values = response.data.unique_values;
          setUniqueValues(values);

          // Initialize selected values
          if (filterConditions[column] && filterConditions[column].length > 0) {
            // Use existing filter
            setSelectedValues(filterConditions[column]);
          } else {
            // Select all by default
            setSelectedValues(values);
          }
        }
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError("Failed to fetch column data");
      console.error("Error fetching column data:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredValues = uniqueValues.filter((val) =>
    String(val).toLowerCase().includes(search.toLowerCase())
  );

  const allChecked =
    filteredValues.length > 0 &&
    filteredValues.every((val) => selectedValues.includes(val));

  const handleSelectAll = (checked) => {
    if (checked) {
      const newSelected = [...new Set([...selectedValues, ...filteredValues])];
      setSelectedValues(newSelected);
    } else {
      const removeSet = new Set(filteredValues);
      setSelectedValues(selectedValues.filter((val) => !removeSet.has(val)));
    }
  };

  const handleDone = () => {
    // Only save if some values are selected
    if (
      selectedValues.length > 0 &&
      selectedValues.length < uniqueValues.length
    ) {
      // Partial selection - save the filter
      onFilterChange(column, selectedValues);
    } else if (selectedValues.length === uniqueValues.length) {
      // All selected - remove the filter (don't send to backend)
      onFilterChange(column, []);
    } else {
      // None selected - keep empty filter
      onFilterChange(column, []);
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        className="p-4 bg-white rounded shadow-xl"
        sx={{
          width: 320,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1500,
        }}
      >
        <div className="flex justify-between items-center mb-2">
          <div className="font-semibold text-lg">{column}</div>
          <Button variant="text" onClick={onClose}>
            <CloseIcon />
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <CircularProgress />
          </div>
        ) : error ? (
          <div className="text-red-600 text-center py-4">{error}</div>
        ) : (
          <>
            <TextField
              variant="outlined"
              size="small"
              placeholder={`Search ${column}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              className="mb-2"
            />

            <div className="mb-2 text-sm text-gray-600">
              {selectedValues.length} of {uniqueValues.length} selected
            </div>

            <div
              className="overflow-y-auto border rounded"
              style={{ maxHeight: 180, padding: 10 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allChecked}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                }
                label="Select All"
              />

              <List dense>
                {filteredValues.map((val, idx) => (
                  <ListItem key={idx} disablePadding>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedValues.includes(val)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedValues([...selectedValues, val]);
                            } else {
                              setSelectedValues(
                                selectedValues.filter((v) => v !== val)
                              );
                            }
                          }}
                        />
                      }
                      label={String(val)}
                    />
                  </ListItem>
                ))}
              </List>
            </div>

            <Button
              onClick={handleDone}
              fullWidth
              variant="contained"
              className="mt-3"
              disabled={selectedValues.length === 0}
            >
              Done ({selectedValues.length} selected)
            </Button>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default function FilterTableView({
  open,
  onClose,
  dbName = "",
  baseTable = "",
  joins = [],
}) {
  const [countPop, setCountPop] = useState(false);
  const [countData, setCountData] = useState({});
  const [query, setQuery] = useState("");
  const [columns, setColumns] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [columnChecks, setColumnChecks] = useState({});
  const [filterConditions, setFilterConditions] = useState({});
  const [openFilter, setOpenFilter] = useState({ open: false, column: null });
  const [loading, setLoading] = useState(false);
  const [payloadFor, setPayloadFor] = useState({});
  const [columnSearch, setColumnSearch] = useState(""); 
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Fetch metadata when modal opens
  useEffect(() => {
    if (open && dbName && baseTable) {
      fetchMetadata();
    }
  }, [open, dbName, baseTable, joins]);

  const fetchMetadata = async () => {
    setLoading(true);

    try {
      const response = await api.post("/schedule_email/view_data_metadata", {
        db_name: dbName,
        base_table: baseTable,
        joins: joins,
      });

      // console.log("Metadata response:", response.data);

      if (response.data.success) {
        const cols = response.data.columns;
        setColumns(cols);
        setRowCount(response.data.row_count);

        // Initialize all columns as checked
        const checks = Object.fromEntries(cols.map((col) => [col, true]));
        setColumnChecks(checks);

        // Initialize empty filter conditions
        const filters = {};
        setFilterConditions(filters);
      } else {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch table metadata",
        severity: "error",
      });
      console.error("Error fetching metadata:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleColumnCheckbox = (col) => {
    setColumnChecks((prev) => ({ ...prev, [col]: !prev[col] }));
  };

  // NEW: Handle Select All for columns
  const handleSelectAllColumns = (checked) => {
    const updatedChecks = Object.fromEntries(
      columns.map((col) => [col, checked])
    );
    setColumnChecks(updatedChecks);
  };

  // Filter columns based on search
  const filteredColumns = columns.filter((col) =>
    col.toLowerCase().includes(columnSearch.toLowerCase())
  );

  // NEW: Check if all columns are selected
  const allColumnsChecked =
    columns.length > 0 && columns.every((col) => columnChecks[col]);

  const handleFilterChange = (col, values) => {
    console.log(`Filter changed for ${col}:`, values);
    setFilterConditions((prev) => ({ ...prev, [col]: values }));
  };

  const handleFilterClick = (col) => {
    setOpenFilter({ open: true, column: col });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSave = async () => {
    // Get selected columns
    const selectedColumns = columns.filter((col) => columnChecks[col]);

    if (selectedColumns.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one column",
        severity: "warning",
      });
      return;
    }

    setLoading(true);

    try {
      // Only send non-empty filter conditions
      const activeFilters = {};
      Object.keys(filterConditions).forEach((col) => {
        if (filterConditions[col] && filterConditions[col].length > 0) {
          activeFilters[col] = filterConditions[col];
        }
      });

      console.log("Sending filters:", activeFilters);

      const payload = {
        db_name: dbName,
        base_table: baseTable,
        joins: joins,
        selected_columns: selectedColumns,
        filter_conditions: activeFilters,
      };

      setPayloadFor(payload);
      // console.log("Save payload:", payload);

      const response = await api.post("/schedule_email/save_filters", payload);

      // console.log("Save response:------>", response.data);

      if (response.data.success) {
        setCountData(response.data.counts);
        setQuery(response.data.query);
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "success",
        });
        onClose();
        setTimeout(() => setCountPop(true), 300);
      } else {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "error",
        });
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to apply filters";
      console.error("Save error:", err.response?.data || err);
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!dbName || !baseTable) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            width: 400,
            background: "white",
            padding: 4,
            borderRadius: 3,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
          }}
          className="shadow-2xl text-center"
        >
          <div className="text-xl font-semibold mb-4">No Data Available</div>
          <p className="text-gray-600 mb-4">
            Please select a database and table first, then click Visualize.
          </p>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <CountPopup
        open={countPop}
        onClose={() => setCountPop(false)}
        data={countData}
        payloadFor={payloadFor}
        query={query}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            width: 600,
            background: "white",
            padding: 4,
            borderRadius: 3,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          }}
          className="shadow-2xl"
        >
          <div className="flex justify-between items-center mb-3">
            <div className="text-xl font-semibold">
              Filter Column ({rowCount} rows)
            </div>
            <Button variant="text" onClick={onClose}>
              <CloseIcon />
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <CircularProgress />
            </div>
          ) : (
            <>
              <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search columns..."
                  value={columnSearch}
                  onChange={(e) => setColumnSearch(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

              <div className="overflow-y-auto" style={{ maxHeight: "50vh" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className="font-semibold">
                        Column<br/>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allColumnsChecked}
                              onChange={(e) =>
                                handleSelectAllColumns(e.target.checked)
                              }
                            />
                          }
                          label="Select all"
                        />
                      </TableCell>
                      <TableCell className="font-semibold">Filter</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredColumns.map((col) => {
                      const filterCount = filterConditions[col]?.length || 0;
                      const hasFilter = filterCount > 0;

                      return (
                        <TableRow key={col}>
                          <TableCell>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={columnChecks[col] || false}
                                  onChange={() => handleColumnCheckbox(col)}
                                />
                              }
                              label={col}
                            />
                          </TableCell>

                          <TableCell>
                            <Button
                              variant="outlined"
                              onClick={() => handleFilterClick(col)}
                              color={hasFilter ? "success" : "primary"}
                            >
                              {hasFilter
                                ? `Filtered (${filterCount})`
                                : "Filter"}
                            </Button>

                            {openFilter.open && openFilter.column === col && (
                              <FilterPopover
                                open={openFilter.open}
                                onClose={() =>
                                  setOpenFilter({ open: false, column: null })
                                }
                                column={col}
                                dbName={dbName}
                                baseTable={baseTable}
                                joins={joins}
                                filterConditions={filterConditions}
                                onFilterChange={handleFilterChange}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  variant="contained"
                  color="info"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Show Counts"}
                </Button>
              </div>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
}
